import * as React from "react";
import { CalculationBasis, CashbackType } from "../../../http/protocol";
import {
  blockedFractionMessage,
  blockingFractionLimit,
  CashbackFractionThresholds,
  isLargeFraction,
  largeFractionThreshold,
} from "../cashbackFractionThresholds";
import { t } from "../../../i18n";

interface Props {
  levels: string[];
  addLevelFraction: string;
  thresholds: CashbackFractionThresholds | undefined;
  // Selects which limit set (rakeback vs cashback) the level fractions are judged against; an unset
  // type falls back to the permissive cashback set.
  rewardType?: CashbackType;
  // Level rows a blocked save attempt flagged; the hard-stop message renders on each of them.
  blockedLevelIndices?: number[];
  onLevelChange: (fraction: string, index: number) => void;
  onAddLevel: (fraction: string) => void;
  onRemoveLevel: (index: number) => void;
  onAddLevelFractionChange: (fraction: string) => void;
}

export const UserLevelsEditor: React.FC<Props> = ({
  levels,
  addLevelFraction,
  thresholds,
  rewardType,
  blockedLevelIndices = [],
  onLevelChange,
  onAddLevel,
  onRemoveLevel,
  onAddLevelFractionChange,
}) => {
  const handleLevelChange = React.useCallback(
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onLevelChange(e.target.value, index);
    },
    [onLevelChange],
  );

  const handleAddLevelFractionChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onAddLevelFractionChange(e.target.value);
    },
    [onAddLevelFractionChange],
  );

  const handleAddLevel = React.useCallback(() => {
    if (addLevelFraction.trim()) {
      onAddLevel(addLevelFraction);
    }
  }, [addLevelFraction, onAddLevel]);

  const handleRemoveLevel = React.useCallback(
    (index: number) => () => {
      onRemoveLevel(index);
    },
    [onRemoveLevel],
  );

  return (
    <div className="card-box">
      <div className="ibox-content">
        <h4 className="ibox-title">User Levels</h4>
        <span>Configure award fraction per User Level. This setting will override the default award fraction configured above.</span>
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Player Level</th>
              <th>Award Fraction</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {levels.map((level, index) => (
              <tr key={index}>
                <td style={{ width: "15%" }}>{index}</td>
                <td>
                  <input
                    type="text"
                    value={level}
                    className="form-control"
                    onChange={handleLevelChange(index)}
                    aria-label={`Award fraction for level ${index}`}
                  />
                  {thresholds && isLargeFraction(Number(level), CalculationBasis.UNWEIGHTED, thresholds, rewardType) && (
                    <div
                      className="alert alert-warning mt-1 mb-0"
                      data-testid={`cashback-v1-level-large-fraction-warning-${index}`}
                    >
                      <strong>{t("cashback.config.large-fraction-warning-title", undefined, "Unusually large fraction")}</strong>{" "}
                      {t(
                        "cashback.config.large-fraction-warning",
                        { threshold: largeFractionThreshold(CalculationBasis.UNWEIGHTED, thresholds, rewardType) },
                        "A fraction of {{threshold}} or more is unusually large and may pay out far more than intended. This is advisory only — you can still save.",
                      )}
                    </div>
                  )}
                  {blockedLevelIndices.includes(index) && (
                    <div
                      className="alert alert-danger mt-1 mb-0"
                      data-testid={`cashback-v1-level-large-fraction-blocked-${index}`}
                    >
                      {blockedFractionMessage("cashback.config.large-fraction-blocked", blockingFractionLimit(thresholds, rewardType))}
                    </div>
                  )}
                </td>
                <td>
                  <button
                    className="btn btn-danger"
                    onClick={handleRemoveLevel(index)}
                    aria-label={`Remove level ${index}`}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td>Add level {levels.length}</td>
              <td>
                <input
                  className="form-control"
                  placeholder="Award Fraction"
                  value={addLevelFraction}
                  onChange={handleAddLevelFractionChange}
                  aria-label="New level award fraction"
                />
              </td>
              <td>
                <button
                  className="btn btn-primary"
                  onClick={handleAddLevel}
                  disabled={!addLevelFraction.trim()}
                  aria-label="Add new level"
                >
                  Add Level
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="hr-line-dashed"></div>
      </div>
    </div>
  );
};
